// Gambio-Lister-Detail-Tab "Bilder" - EIN unified Bilder-Tab für einen Artikel (läuft auch
// im Vario-Slot): Aktiv-Haken (= Gambio-Freigabe/Whitelist) direkt vor jedem Bild, Alt-Text,
// Reihenfolge per nativem Drag & Drop (kein Tab-Umschalten mehr zwischen "Aktive Bilder" und
// "Freigeben" - beides ist jetzt eine gemeinsame Liste über ALLE Artikelbilder).
// Speichern triggert gambio-release-images → Worker gambio.ensure_dms_share
// + Alt-Text + uiSort, public_url zurück in gambio.article_allowed_images.
//
// Löschen gibt es hier bewusst NICHT (mehr) - Bilder werden ausschließlich im Vario-DMS
// gelöscht, nicht aus der Gambio-Oberfläche heraus.
//
// CSS: .kli-* sind otto-lokal (NICHT im shared Design-Package) → hier kopiert.
// callFn: direkt als lokaler Helper (kein import aus otto-Paket möglich).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { infoBadge } from '../../lib/info-badge.js';
import { varioPublicDmsUrl } from '../../lib/vario-links.js';

const BILDER_BADGE = infoBadge('Der Aktiv-Haken bestimmt sichtbare Bilder, Ziehen ändert die Reihenfolge. Löschen nur im VARIO-DMS.');

export interface ImagesTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

export interface ImagesTabListing {
  vario_article_id: string;
  mode?: 'parent' | 'standalone' | 'variant';
  parentListingId?: string | null;
}

interface ImageRow {
  resourceId: string;
  alias: string;
  mimeType: string;
  fileSize: number | null;
  released: boolean;
  sortOrder: number | null;
  varioUiSort: number | null;
  publicUrl: string | null;
  // Lokaler Edit-State (mehrsprachig, aus dem Backend altTextByLang):
  altTextByLang: Record<string, string>;
}

const COMPONENT_STYLES = `
  .kli-shell { padding: 1em; display: flex; flex-direction: column; gap: 0.8em; }
  /* Klare Trennung der drei Bereiche (Überschrift-Kasten · Bild-Ablage · Bilderliste):
     die Kästen füllen sonst den Container ohne sichtbaren Abstand. */
  .kli-body { display: flex; flex-direction: column; gap: 1.6em; }
  .kli-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.6em 0.8em;
    background: var(--v-color-surface, #fff);
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: var(--v-border-radius-card, 12px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .kli-list { display: flex; flex-direction: column; gap: 0.4em; }
  .kli-row {
    display: grid;
    grid-template-columns: 24px 28px 96px 1fr;
    gap: 1em;
    align-items: center;
    padding: 0.6em 0.8em;
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: 8px;
    background: var(--v-color-surface, #fff);
    border-left: 3px solid var(--v-positive, #84b933);
    transition: opacity 0.15s, border-color 0.15s;
  }
  .kli-row--variant { grid-template-columns: 24px 34px 96px 1fr; }
  .kli-row--dragging { opacity: 0.4; }
  .kli-row--drag-over { border-color: var(--v-positive, #84b933); border-style: dashed; }
  .kli-drag {
    display: flex; align-items: center; justify-content: center;
    color: var(--v-color-text-muted, #999); cursor: grab; user-select: none;
  }
  .kli-drag:active { cursor: grabbing; }
  .kli-active-wrap { display: flex; align-items: center; justify-content: center; cursor: pointer; }
  .kli-active { width: 18px; height: 18px; cursor: pointer; }
  .kli-thumb {
    width: 96px; height: 96px; object-fit: cover;
    border-radius: 6px; border: 1px solid var(--v-color-border, #ddd);
    background: var(--v-color-surface-muted, #f7f7f7);
  }
  .kli-thumb.kli-thumb-loading {
    background: linear-gradient(110deg, #f0f0f0 8%, #e3e3e3 18%, #f0f0f0 33%);
    background-size: 200% 100%;
    animation: kli-shimmer 1.5s linear infinite;
  }
  @keyframes kli-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  .kli-meta { display: flex; flex-direction: column; gap: 0.35em; min-width: 0; }
  .kli-alias { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kli-slot {
    display: inline-block; padding: 0.1em 0.45em; margin-right: 0.3em;
    background: var(--v-positive-soft, #e8f3d6); color: var(--v-positive, #84b933);
    border-radius: 4px; font-size: 0.82em; font-weight: 600;
  }
  .kli-sub { font-size: 0.78em; color: var(--v-color-text-muted, #666); }
  .kli-alt-input {
    width: 100%; padding: 0.4em 0.6em;
    border: 1px solid var(--v-color-border, #ddd); border-radius: 4px;
    font-size: 0.92em;
  }
  .kli-empty { padding: 1.5em; text-align: center; color: var(--v-color-text-muted, #666); }
  .kli-toggle-thumb { width: 64px; height: 64px; object-fit: cover; border-radius: 6px; border: 1px solid var(--v-color-border, #ddd); background: var(--v-color-surface-muted, #f7f7f7); }
  /* Upload-Zone */
  .kli-upload-zone {
    border: 2px dashed var(--v-color-border, #ccc); border-radius: 8px;
    padding: 1em 1.2em; display: flex; align-items: center; gap: 0.8em;
    cursor: pointer; transition: border-color 0.15s, background 0.15s;
    background: var(--v-color-surface, #fff);
  }
  .kli-upload-zone:hover, .kli-upload-zone.kli-upload-zone--drag { border-color: var(--v-positive, #84b933); background: var(--v-positive-soft, #f0f7e6); }
  .kli-upload-zone input[type=file] { display: none; }
  .kli-upload-zone-label { font-size: 0.9em; color: var(--v-color-text-muted, #666); }
`;

// Matrix-CSS: Port aus Otto (apps/frontend-vario-otto/src/styles-lister.css ~693-868),
// Order-Zellen-Modell statt Checkboxen. .otto-lv2-Scope entfernt (Gambio hat den nicht),
// go-brand-Farbtokens statt --otto-accent/--otto-accent-bg. Klassenamen wie im Otto-Markup
// (kl-ordcell/kl-ordwrap/kl-ordmini/kl-matrix/kl-matrix-wrap/kl-mthumb/kl-img-note/kl-colcb/
// kl-mcb/kl-malle/kl-mdms/kl-mx-lft) - component-local, wie die übrigen .kli-* hier kopiert.
const MATRIX_STYLES = `
  .kli-img-note {
    font-size: 12px;
    color: var(--v-color-text-muted, #666);
    margin-bottom: 0.6em;
    line-height: 1.5;
  }
  .kli-img-note b { color: var(--v-color-text, #222); font-weight: 600; }

  .kli-matrix-wrap {
    overflow-x: auto;
    border: 1px solid var(--v-color-border-soft, #eee);
    border-radius: 8px;
  }
  .kli-matrix { border-collapse: collapse; min-width: 100%; }
  .kli-matrix th,
  .kli-matrix td {
    padding: 11px 12px;
    border-bottom: 1px solid var(--v-color-border-soft, #eee);
    font-size: 12px;
    white-space: nowrap;
  }
  .kli-matrix thead th {
    text-align: center;
    font-weight: 600;
    color: var(--v-color-text, #222);
    background: var(--v-color-surface-muted, #f7f7f7);
    position: sticky;
    top: 0;
    z-index: 2;
  }
  .kli-matrix thead th.kl-mx-lft { text-align: left; }
  /* Erste Spalte („Bild") beim horizontalen Scrollen fixiert, damit das Thumbnail im Blick
     bleibt, wenn mehr Artikel-Spalten da sind als auf die Seite passen. Header-Ecke z-index 3
     (über sticky-Header + sticky-Body-Spalte), Body-Zelle opak + rechter Trenner, damit die
     scrollenden Spalten sauber dahinter verschwinden. */
  .kli-matrix thead th:first-child { left: 0; z-index: 3; }
  .kli-matrix tbody td:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    background: var(--v-color-surface, #fff);
    box-shadow: 1px 0 0 var(--v-color-border-soft, #eee);
  }
  .kli-mx-imgcell,
  .kli-mx-metacell,
  .kli-mx-seocell { text-align: left; vertical-align: middle; }
  .kli-mx-center { text-align: center; padding: 8px 12px; }

  /* Spalten-Kopf-Checkbox (Artikelnummer monospace, Vater fett) */
  .kl-colcb {
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    font-family: ui-monospace, monospace;
    font-size: 10.5px;
    color: var(--v-color-text, #222);
    cursor: pointer;
  }
  .kl-colcb.is-vater { font-family: inherit; font-weight: 600; }
  .kl-mcb { width: 17px; height: 17px; accent-color: var(--v-positive, #84b933); cursor: pointer; }

  /* Thumbnail (Platzhalter + geladenes Bild) */
  .kl-mthumb {
    width: 46px;
    height: 46px;
    border-radius: 6px;
    background: var(--v-color-surface-muted, #f7f7f7);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-color-text-muted, #999);
  }
  .kl-mthumb.kli-thumb-loading {
    background: linear-gradient(110deg, #f0f0f0 8%, #e3e3e3 18%, #f0f0f0 33%);
    background-size: 200% 100%;
    animation: kli-shimmer 1.5s linear infinite;
  }
  .kl-mthumb-img {
    width: 46px;
    height: 46px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid var(--v-color-border, #ddd);
    display: block;
  }

  /* „alle"-Button + DMS-Alias */
  .kl-malle {
    font-family: inherit;
    font-size: 10.5px;
    font-weight: 600;
    color: var(--v-color-text, #222);
    background: var(--v-color-surface, #fff);
    border: 1px solid var(--v-color-border, #ddd);
    border-radius: 6px;
    padding: 3px 9px;
    cursor: pointer;
  }
  .kl-malle:hover { border-color: var(--v-positive, #84b933); color: var(--v-positive, #84b933); }
  .kl-mdms {
    font-family: ui-monospace, monospace;
    font-size: 9.5px;
    color: var(--v-color-text-muted, #999);
    margin-top: 4px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* SEO-Titel-Input (Gambio-spezifische Spalte, kein Otto-Aequivalent) */
  .kli-mx-seo { width: 150px; }

  /* ---- Order-Zelle (+ bzw. nummerierte „on"-Zelle + ▲▼-Mini) ---- */
  .kl-ordwrap { display: inline-flex; align-items: center; gap: 5px; }
  .kl-ordcell {
    width: 30px;
    height: 30px;
    border: 1.5px dashed var(--v-color-border, #ddd);
    border-radius: 50%;
    background: var(--v-color-surface, #fff);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
    font-size: 15px;
    font-weight: 400;
    color: var(--v-color-text-muted, #999);
    cursor: pointer;
    transition: all 0.14s;
  }
  .kl-ordcell:hover {
    border-color: var(--v-positive, #84b933);
    color: var(--v-positive, #84b933);
    background: var(--v-positive-soft, #eef6e0);
    border-style: solid;
  }
  .kl-ordcell.is-on {
    border-style: solid;
    border-color: transparent;
    /* Reihenfolge-Badge: VARIO-Gruen-Verlauf, wie Otto */
    background: linear-gradient(150deg, #94c93f, #6fa028);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 2px 6px -1px rgba(132, 185, 51, 0.5);
  }
  .kl-ordcell.is-on:hover { filter: brightness(1.05); }
  .kl-ordmini {
    display: flex;
    flex-direction: column;
    gap: 0;
    opacity: 0;
    transition: opacity 0.14s;
  }
  .kli-mx-center:hover .kl-ordmini { opacity: 1; }
  .kl-ordmini button {
    width: 16px;
    height: 13px;
    border: none;
    background: none;
    color: var(--v-color-text-muted, #999);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 3px;
    font-family: inherit;
  }
  .kl-ordmini button .v-icon { font-size: 9px; line-height: 1; }
  .kl-ordmini button:hover:not(:disabled) { color: var(--v-positive, #84b933); background: var(--v-positive-soft, #eef6e0); }
  .kl-ordmini button:disabled { opacity: 0.25; cursor: default; }

  .kli-mx-toolbar { display:flex; justify-content:flex-end; padding:0.4em 0.6em; }
`;

interface MatrixTarget { articleId: string; label: string; isParent: boolean }
interface MatrixImage { resourceId: string; owningArticleId: string; alias: string; mimeType: string; publicUrl: string | null; seoTitleByLang: Record<string, string>; assignedTo: string[]; assignedOrder: Record<string, number> }

// Sprach-Reihenfolge fuers Rendern: Default/'de' zuerst, dann alphabetisch (Muster aus
// list-additional-fields.ts). Dedupe ueber Set.
function sortLangs(langs: string[]): string[] {
  return [...new Set(langs)].sort((a, b) => (a === 'de' ? -1 : b === 'de' ? 1 : a.localeCompare(b)));
}

// Wiederverwendbarer Per-Sprache-Zellen-Renderer: je aktiver Shop-Sprache ein gestapeltes
// Input mit Sprach-Kuerzel-Pill als Praefix. Nutzt bestehende Design-System-Klassen
// (kli-alt-input + v-pill), keine neuen Praefixe. `placeholderBase` = "SEO-Titel" (Matrix)
// bzw. "Alt-Text" (Bilder-/Varianten-Tab); `extraClass` fuer Matrix-Breite (kli-mx-seo).
// Jedes Input traegt data-rid + data-lang, damit die Wiring-Handler den Wert der richtigen
// Sprache in die {lang: text}-Map schreiben.
function altLangInputs(
  resourceId: string,
  valById: Record<string, string>,
  langs: string[],
  placeholderBase: string,
  extraClass = '',
): string {
  return sortLangs(langs).map((lang) => `
    <label class="kli-alt-lang" style="display:flex;gap:4px;align-items:center;margin:1px 0;">
      <span class="v-pill v-pill--muted" style="font-size:9px;min-width:20px;text-align:center;">${escapeHtml(lang)}</span>
      <input type="text" class="kli-alt-input${extraClass ? ` ${extraClass}` : ''}" data-rid="${escapeHtml(resourceId)}" data-lang="${escapeHtml(lang)}"
             value="${escapeHtml(valById[lang] ?? '')}" placeholder="${escapeHtml(placeholderBase)} (${escapeHtml(lang)})" maxlength="250">
    </label>`).join('');
}

async function callFn<T>(
  ctx: ImagesTabCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// Erlaubte Bild-MIME-Typen (Spiegel des accept-Attributs + Gambio-Allowlist).
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_IMAGE_LABEL = 'JPG, PNG, GIF oder WebP';

// Manche Dateien liefern kein file.type (leerer String) → Endungs-Fallback.
function isAllowedImage(file: File): boolean {
  if (file.type) return ALLOWED_IMAGE_TYPES.includes(file.type);
  return /\.(jpe?g|png|gif|webp)$/i.test(file.name);
}

// Übersetzt eine technische Fehlerursache in eine kundenfreundliche Meldung.
function friendlyUploadError(technical: string): string {
  const t = technical.toLowerCase();
  // Bewusst spezifische Tokens (kein nacktes 'type') - sonst würde ein unverwandter
  // Backend-Fehler, der zufällig „type" enthält, als Format-Fehler fehlgedeutet.
  if (t.includes('unsupported') || t.includes('mime') || t.includes('format')
      || t.includes('avif') || t.includes('heic') || t.includes('content type')
      || t.includes('contenttype') || t.includes('content-type')
      || t.includes('file type') || t.includes('filetype')
      || t.includes('dateityp') || t.includes('dateiformat')) {
    return `Dieses Dateiformat wird nicht unterstützt. Bitte lade ein Bild als ${ALLOWED_IMAGE_LABEL} hoch.`;
  }
  if (t.includes('413') || t.includes('too large') || t.includes('size') || t.includes('groß')) {
    return 'Die Datei ist zu groß. Bitte lade ein kleineres Bild hoch.';
  }
  return `Der Upload hat nicht geklappt. Bitte prüfe die Datei und versuche es erneut - unterstützt werden ${ALLOWED_IMAGE_LABEL}.`;
}

// Lädt EIN Bild in das Vario-DMS des angegebenen Artikels. Prüft zuerst den Typ
// clientseitig (kein Backend-Call bei falschem Typ). Gibt bei Fehlern eine
// kundenfreundliche Meldung zurück; die technische Ursache landet in der Konsole.
async function uploadImageFile(
  ctx: ImagesTabCtx,
  file: File,
  articleId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!isAllowedImage(file)) {
    console.warn('[gambio-list-images] upload rejected (client type check):', file.type || '(kein type)', file.name);
    return { ok: false, message: `Dieses Dateiformat wird nicht unterstützt. Bitte lade ein Bild als ${ALLOWED_IMAGE_LABEL} hoch.` };
  }
  const fd = new FormData();
  fd.append('file', file);
  fd.append('articleId', articleId);
  try {
    const r = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-article-image-upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ctx.session.token}` },
      // KEIN Content-Type - Browser setzt multipart/form-data + boundary automatisch.
      body: fd,
    });
    if (!r.ok) {
      const raw = await r.text().catch(() => '');
      let technical = `HTTP ${r.status}`;
      try {
        const j = JSON.parse(raw);
        if (j?.error?.message) technical = `${j.error.code ?? '?'}: ${j.error.message}`;
      } catch { if (raw) technical = raw; }
      console.warn('[gambio-list-images] upload failed:', technical);
      return { ok: false, message: friendlyUploadError(technical) };
    }
    return { ok: true };
  } catch (e) {
    const technical = (e as Error).message;
    console.warn('[gambio-list-images] upload error:', technical);
    return { ok: false, message: friendlyUploadError(technical) };
  }
}

export async function renderImagesTab(
  host: HTMLElement,
  ctx: ImagesTabCtx,
  listing: ImagesTabListing,
): Promise<void> {
  host.innerHTML = `<style>${COMPONENT_STYLES}</style><div class="kli-shell"><div class="kli-empty">Lade Vario-DMS-Bilder …</div></div>`;

  const mode = listing.mode ?? 'standalone';
  if (mode === 'parent' && listing.parentListingId) {
    return renderParentMatrix(host, ctx, listing.parentListingId);
  }
  if (mode === 'variant' && listing.parentListingId) {
    return renderVariantColumn(host, ctx, listing.vario_article_id, listing.parentListingId);
  }
  type Loaded = ImageRow;
  const res = await callFn<{ ok: boolean; images: Loaded[]; languages?: string[] }>(ctx, 'gambio-article-images', {
    articleId: listing.vario_article_id,
  });
  if (!res.ok) {
    host.innerHTML = `<div class="kli-shell"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`;
    return;
  }
  const languages = sortLangs(res.value.languages ?? ['de', 'en']);

  // rows ist der EINE gemeinsame State für die ganze Liste (aktiv + inaktiv). Die
  // Position im Array IST die Anzeige-/Push-Reihenfolge - Drag & Drop verschiebt
  // Einträge direkt darin (siehe reorderRows). Initial: freigegebene Bilder zuerst
  // (nach vorhandenem sortOrder/varioUiSort sortiert), danach nicht-freigegebene
  // (nach varioUiSort als stabiler Tiebreaker).
  const rows: ImageRow[] = (res.value.images ?? []).map((img) => ({
    ...img,
    altTextByLang: { ...(img.altTextByLang ?? {}) },
  }));
  rows.sort((a, b) => {
    if (a.released !== b.released) return a.released ? -1 : 1;
    if (a.released) return (a.sortOrder ?? a.varioUiSort ?? 0) - (b.sortOrder ?? b.varioUiSort ?? 0);
    return (a.varioUiSort ?? 0) - (b.varioUiSort ?? 0);
  });

  let dirty = false;

  // Cache der via Proxy geladenen Vorschau-Blob-URLs pro resourceId. Lebt fuer die
  // Dauer dieses Dialogs. Verhindert, dass jeder Re-Render / Checkbox-Toggle / Drag
  // dieselben Bilder erneut durch den DMS-Proxy laedt - sonst Vario-429 (Rate-Limit).
  const previewCache = new Map<string, string>();

  // Liefert das <img>-Markup fuer eine Vorschau: eine bereits gecachte Proxy-Blob-URL,
  // sonst einen Lazy-Platzhalter, den loadPreviewsViaProxy spaeter EINMAL befuellt und
  // cacht. draggable="false", damit der native Bild-Drag des Browsers nicht mit unserem
  // Zeilen-Drag&Drop kollidiert.
  //
  // Anzeigeweg, in dieser Reihenfolge:
  //   1. bereits geladene Blob-URL aus dem Cache,
  //   2. DIREKT aus dem DMS ueber die Freigabe (`varioPublicDmsUrl`) — der Browser holt alle Bilder
  //      parallel und cacht sie, ohne Proxy und ohne OAuth,
  //   3. Lazy-Platzhalter, den `loadPreviewsViaProxy` sequenziell ueber den DMS-Proxy befuellt.
  //
  // Warum (2) neu ist: `public_url` galt als unbrauchbar, weil Vario darauf 500 „can't determine
  // tenant from url" liefert. Geprobt am 27.08.2026 mit DERSELBEN Adresse, nur anderem Host:
  // numerischer Realm-Host (`tn0000002012.vario.cloud`) → 500, benannte Subdomain
  // (`pfannenshop.vario.cloud`) → 200 image/png. Der Worker baut die Adresse aus dem
  // Vario-API-Host, also dem numerischen; im Browser laufen wir als iframe IN der Tenant-Instanz und
  // kennen den benannten. `varioPublicDmsUrl` setzt deshalb den Pfad auf den Embedder-Origin.
  //
  // Warum der Proxy bleibt: er ist der Rueckfall fuer fehlende Freigabe, fehlende Dateiendung
  // (dann 404) und den Standalone-Fall ohne Embedder. Schlaegt die direkte Adresse im Browser fehl,
  // greift der `error`-Lauscher unten und schiebt die Zeile in den Proxy-Pfad.
  function previewImg(r: ImageRow, thumbClass: string): string {
    const ready = previewCache.get(r.resourceId) ?? null;
    if (ready) return `<img class="${thumbClass}" src="${escapeHtml(ready)}" alt="" draggable="false">`;
    const direkt = varioPublicDmsUrl(r.publicUrl);
    if (direkt) {
      return `<img class="${thumbClass} img-dms-direct" src="${escapeHtml(direkt)}" `
        + `data-rid="${escapeHtml(r.resourceId)}" alt="" draggable="false">`;
    }
    return `<img class="${thumbClass} kli-thumb-loading img-proxy-lazy" data-rid="${escapeHtml(r.resourceId)}" alt="" draggable="false">`;
  }

  // Vollständige releases-Payload aus dem gemeinsamen State: iteriert `rows` in der
  // AKTUELLEN Anzeige-/Drag-Reihenfolge, filtert auf released und vergibt fortlaufenden
  // sortOrder (0..N). Was hier fehlt (= nicht released), löscht das Backend (Diff-Upsert).
  function buildReleases(): Array<{ varioResourceId: string; sortOrder: number; mimeType: string; alias: string; altTextByLang: Record<string, string> }> {
    return rows
      .filter((r) => r.released)
      .map((r, idx) => ({
        varioResourceId: r.resourceId,
        sortOrder: idx,
        mimeType: r.mimeType,
        alias: r.alias,
        altTextByLang: r.altTextByLang,
      }));
  }

  // ── Upload-Zone ────────────────────────────────────────────────────────────
  // Rendert eine Drag-Drop/Datei-Input-Fläche. Bei Dateiauswahl POST an
  // vario-article-image-upload als multipart/form-data (kein Content-Type-Header
  // gesetzt → Browser fügt boundary automatisch hinzu). Nach Erfolg: Neustart
  // des gesamten Tabs via renderImagesTab + showToast.
  function renderUploadZone(): string {
    return `
      <div class="kli-upload-zone" id="kli-upload-zone" role="button" tabindex="0"
           aria-label="Bild hochladen - klicken oder hierher ziehen">
        <input type="file" id="kli-upload-input" accept="image/jpeg,image/png,image/gif,image/webp">
        <span class="v-icon fa-regular fa-cloud-arrow-up" style="font-size:1.4em;color:var(--v-positive,#84b933);"></span>
        <div class="kli-upload-zone-label">
          <strong>Bild hochladen</strong><br>
          <span>Klicken oder Datei hier ablegen (JPG, PNG, GIF, WebP)</span>
        </div>
        <span id="kli-upload-busy" hidden><span class="v-icon fa-regular fa-spinner-third fa-spin"></span></span>
      </div>`;
  }

  function wireUploadZone(shell: HTMLElement): void {
    const zone = shell.querySelector<HTMLElement>('#kli-upload-zone');
    const inp = shell.querySelector<HTMLInputElement>('#kli-upload-input');
    const busy = shell.querySelector<HTMLElement>('#kli-upload-busy');
    if (!zone || !inp) return;
    // After the null-guard above, capture non-null references for use inside closures.
    const zoneEl: HTMLElement = zone;

    // Klick auf Zone öffnet Dateiauswahl.
    zoneEl.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('input[type=file]')) return;
      inp.click();
    });
    zoneEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); }
    });

    // Drag-over / drop.
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('kli-upload-zone--drag'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('kli-upload-zone--drag'));
    zoneEl.addEventListener('drop', (e) => {
      e.preventDefault();
      zoneEl.classList.remove('kli-upload-zone--drag');
      const file = e.dataTransfer?.files[0];
      if (file) void doUpload(file);
    });

    inp.addEventListener('change', () => {
      const file = inp.files?.[0];
      if (file) void doUpload(file);
    });

    async function doUpload(file: File): Promise<void> {
      if (busy) busy.hidden = false;
      zoneEl.style.pointerEvents = 'none';
      try {
        const res = await uploadImageFile(ctx, file, listing.vario_article_id);
        if (!res.ok) { showToast(res.message, 'error'); return; }
        showToast('Bild hochgeladen.', 'success');
        void renderImagesTab(host, ctx, listing);
      } finally {
        if (busy) busy.hidden = true;
        zoneEl.style.pointerEvents = '';
      }
    }
  }

  // ── Bilder-Liste (Aktiv-Haken + Drag & Drop, EIN Tab) ───────────────────────

  function rerenderAll(): void {
    const shell = host.querySelector<HTMLElement>('.kli-body');
    if (!shell) return;
    if (rows.length === 0) {
      shell.innerHTML = `
        <div class="kli-header"><strong>Bilder</strong> ${BILDER_BADGE}</div>
        ${renderUploadZone()}
        <div class="kli-empty">
          Keine Bilder am Artikel. Bitte erst in Vario unter
          <em>Artikel-Detail → Anlagen</em> Bilder hochladen (jpg/png/gif).
        </div>`;
      wireUploadZone(shell);
      return;
    }

    const releasedCount = rows.filter((r) => r.released).length;
    const countLabel = `${releasedCount} von ${rows.length} ${rows.length === 1 ? 'Bild' : 'Bildern'} aktiv`;

    // Slot-Label ("Hauptbild"/"Bild N") nur für aktive (released) Bilder, gezählt in
    // der aktuellen Anzeige-Reihenfolge (= rows-Reihenfolge).
    let activeCounter = 0;
    const activeIdxByRid = new Map<string, number>();
    for (const r of rows) {
      if (r.released) activeIdxByRid.set(r.resourceId, activeCounter++);
    }

    shell.innerHTML = `
      <div class="kli-header">
        <div>
          <strong>Bilder</strong> ${BILDER_BADGE}
          <span class="v-text-muted" style="font-size:0.85em;margin-left:0.5em;">${countLabel}</span>
        </div>
        <div style="display:flex;gap:0.4em;">
          <button type="button" class="v-button" id="kli-reset" ${dirty ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-rotate-left"></span> Zurücksetzen
          </button>
          <button type="button" class="v-button v-button--save" id="kli-save" ${dirty ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
          </button>
        </div>
      </div>
      ${renderUploadZone()}
      <div class="kli-list">
        ${rows.map((r) => renderRow(r, activeIdxByRid.get(r.resourceId) ?? null)).join('')}
      </div>
    `;
    wireRows(shell);
    wireUploadZone(shell);
  }

  function renderRow(r: ImageRow, activeIdx: number | null): string {
    const sizeKb = r.fileSize ? `${Math.round(r.fileSize / 1024)} KB` : '';
    const previewInner = previewImg(r, 'kli-thumb');
    const slotLabel = activeIdx === null ? '' : (activeIdx === 0 ? 'Hauptbild' : `Bild ${activeIdx}`);
    const checkboxHtml = `
        <label class="kli-active-wrap" title="Für Gambio aktiv (freigegeben)">
          <input type="checkbox" class="kli-active" data-rid="${escapeHtml(r.resourceId)}" draggable="false" ${r.released ? 'checked' : ''}>
        </label>`;
    return `
      <div class="kli-row" draggable="true" data-rid="${escapeHtml(r.resourceId)}">
        <span class="kli-drag" title="Ziehen zum Sortieren"><span class="v-icon fa-regular fa-grip-vertical"></span></span>
        ${checkboxHtml}
        ${previewInner}
        <div class="kli-meta">
          <div class="kli-alias" title="${escapeHtml(r.alias)}">${slotLabel ? `<span class="kli-slot">${slotLabel}</span> · ` : ''}${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>
          ${altLangInputs(r.resourceId, r.altTextByLang, languages, 'Alt-Text')}
        </div>
      </div>`;
  }

  function wireRows(shell: HTMLElement): void {
    shell.querySelectorAll<HTMLInputElement>('input.kli-alt-input').forEach((inp) => {
      inp.addEventListener('input', () => {
        const r = rows.find((x) => x.resourceId === inp.dataset['rid']);
        const lang = inp.dataset['lang'];
        if (r && lang) { r.altTextByLang[lang] = inp.value; dirty = true; updateSaveBtn(shell); }
      });
    });
    shell.querySelectorAll<HTMLInputElement>('input.kli-active').forEach((cb) => {
      cb.addEventListener('change', () => {
        const r = rows.find((x) => x.resourceId === cb.dataset['rid']);
        if (!r) return;
        r.released = cb.checked;
        dirty = true;
        rerenderAll();
      });
    });
    wireDragAndDrop(shell);
    shell.querySelector<HTMLButtonElement>('#kli-save')?.addEventListener('click', () => void doSave());
    shell.querySelector<HTMLButtonElement>('#kli-reset')?.addEventListener('click', () => void renderImagesTab(host, ctx, listing));
    beobachteDirekteBilder(shell);
    void loadPreviewsViaProxy(shell);
  }

  // Natives HTML5 Drag & Drop (keine Library): der ganze `.kli-row` ist draggable.
  // dragstart merkt sich die resourceId + setzt die dataTransfer-Payload; dragover
  // erlaubt den Drop und markiert das Ziel; drop verschiebt den Eintrag im
  // `rows`-Array an die neue Position (Splice). Checkbox + Alt-Text-Input sind
  // draggable="false", damit Klicken/Tippen darin nicht als Drag-Start greift.
  function wireDragAndDrop(shell: HTMLElement): void {
    const list = shell.querySelector<HTMLElement>('.kli-list');
    if (!list) return;
    let draggedRid: string | null = null;
    list.querySelectorAll<HTMLElement>('.kli-row').forEach((rowEl) => {
      rowEl.addEventListener('dragstart', (ev) => {
        draggedRid = rowEl.dataset['rid'] ?? null;
        rowEl.classList.add('kli-row--dragging');
        if (ev.dataTransfer) {
          ev.dataTransfer.effectAllowed = 'move';
          ev.dataTransfer.setData('text/plain', draggedRid ?? '');
        }
      });
      rowEl.addEventListener('dragend', () => {
        rowEl.classList.remove('kli-row--dragging');
        list.querySelectorAll('.kli-row--drag-over').forEach((el) => el.classList.remove('kli-row--drag-over'));
        draggedRid = null;
      });
      rowEl.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
        if (rowEl.dataset['rid'] !== draggedRid) rowEl.classList.add('kli-row--drag-over');
      });
      rowEl.addEventListener('dragleave', () => rowEl.classList.remove('kli-row--drag-over'));
      rowEl.addEventListener('drop', (ev) => {
        ev.preventDefault();
        rowEl.classList.remove('kli-row--drag-over');
        const targetRid = rowEl.dataset['rid'] ?? null;
        const sourceRid = draggedRid ?? ev.dataTransfer?.getData('text/plain') ?? null;
        if (!sourceRid || !targetRid || sourceRid === targetRid) return;
        reorderRows(sourceRid, targetRid);
      });
    });
  }

  // Verschiebt den gezogenen Eintrag im gemeinsamen `rows`-Array an die Position
  // des Ziel-Eintrags. buildReleases() liest die Push-Reihenfolge direkt aus
  // dieser Array-Position (kein separates sortOrder-Feld muss hier mitgeführt
  // werden - das passiert erst beim Speichern in buildReleases).
  function reorderRows(sourceRid: string, targetRid: string): void {
    const fromIdx = rows.findIndex((r) => r.resourceId === sourceRid);
    const toIdx = rows.findIndex((r) => r.resourceId === targetRid);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const [moved] = rows.splice(fromIdx, 1);
    rows.splice(toIdx, 0, moved!);
    dirty = true;
    rerenderAll();
  }

  function updateSaveBtn(shell: HTMLElement): void {
    const save = shell.querySelector<HTMLButtonElement>('#kli-save');
    const reset = shell.querySelector<HTMLButtonElement>('#kli-reset');
    if (save) save.disabled = !dirty;
    if (reset) reset.disabled = !dirty;
  }

  /**
   * Faengt fehlgeschlagene DIREKTE DMS-Adressen ab und schiebt die Zeile in den Proxy-Pfad.
   * `error` steigt nicht auf, deshalb ein Lauscher in der Capture-Phase auf der Huelle.
   * Nur EIN Versuch je Bild (`img-dms-direct` wird entfernt), sonst dreht es sich im Kreis.
   */
  function beobachteDirekteBilder(shell: HTMLElement): void {
    shell.addEventListener('error', (ev) => {
      const el = ev.target as HTMLElement | null;
      if (!(el instanceof HTMLImageElement) || !el.classList.contains('img-dms-direct')) return;
      el.classList.remove('img-dms-direct');
      el.classList.add('kli-thumb-loading', 'img-proxy-lazy');
      el.removeAttribute('src');
      void loadPreviewsViaProxy(shell);
    }, true);
  }

  async function loadPreviewsViaProxy(shell: HTMLElement): Promise<void> {
    const imgs = Array.from(shell.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy'));
    // Sequenziell (Concurrency 1) statt parallel - der DMS-Proxy macht pro Bild einen
    // Vario-DMS-Call + OAuth-Refresh; ein Burst ueberschreitet das Vario-Rate-Limit (429).
    // Jede geladene Blob-URL wird in previewCache abgelegt, sodass Re-Render / Toggles
    // dasselbe Bild NICHT erneut laden (previewImg() rendert dann direkt aus dem Cache).
    for (const imgEl of imgs) {
      const rid = imgEl.dataset['rid'];
      if (!rid) continue;
      if (!imgEl.isConnected) continue;
      const cached = previewCache.get(rid);
      if (cached) {
        imgEl.src = cached;
        imgEl.classList.remove('kli-thumb-loading');
        continue;
      }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: listing.vario_article_id, resourceId: rid, marketplace: 'gambio' }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob());
        previewCache.set(rid, url);
        if (!imgEl.isConnected) continue;
        imgEl.src = url;
        imgEl.classList.remove('kli-thumb-loading');
      } catch (e) {
        console.warn('[gambio-list-images] preview proxy failed for', rid, (e as Error).message);
      }
    }
  }

  async function doSave(): Promise<void> {
    const saveBtn = host.querySelector<HTMLButtonElement>('#kli-save');
    if (saveBtn) saveBtn.disabled = true;
    const releases = buildReleases();
    const res = await callFn<{ ok: boolean; inserted: number; updated: number; deleted: number; enqueuedShareJobs?: number; enqueuedMetaJobs?: number }>(
      ctx,
      'gambio-release-images',
      { articleId: listing.vario_article_id, releases },
    );
    if (!res.ok) {
      openErrorModal({
        title: 'Speichern fehlgeschlagen',
        items: [{ message: res.error }],
        context: { action: 'gambio-release-images', articleId: listing.vario_article_id, releaseCount: releases.length },
        json: res,
      });
      if (saveBtn) saveBtn.disabled = false;
      return;
    }
    const v = res.value;
    showToast(
      `Bilder gespeichert (${v.inserted} neu, ${v.updated} sortiert, ${v.deleted} entfernt${v.enqueuedShareJobs ? `, ${v.enqueuedShareJobs} neue Shares` : ''}${v.enqueuedMetaJobs ? `, ${v.enqueuedMetaJobs} DMS-Updates` : ''}).`,
      'success',
    );
    dirty = false;
    void renderImagesTab(host, ctx, listing);
  }

  // ── Shell ─────────────────────────────────────────────────────────────────

  function renderShell(): void {
    host.innerHTML = `
      <style>${COMPONENT_STYLES}</style>
      <div class="kli-shell">
        <div class="kli-body"></div>
      </div>`;
    rerenderAll();
  }

  renderShell();
}

// ============================================================================
// Variantenvater: Bild-Zuordnungs-Matrix - Otto-Parität (RD5-Port). Zeilen = alle
// Bilder Vater+Kinder, Spalten = Vater + jede Variante (aus der Vario-Struktur), Zelle = "+"-Button
// (zuordnen) ODER gruene nummerierte "on"-Zelle (= Anzeige-Reihenfolge je Spalte)
// mit ▲▼-Mini (vor/zurück). Spaltenkopf zeigt die Varianten-Artikelnummer
// (Backend-Label, siehe gambio-variant-image-matrix handleList). Kein separater
// "Reihenfolge (Vater)"-Subtab mehr - die Vater-Spalte der Matrix ordnet die
// Vater-Bilder bereits selbst (order-Zellen je Spalte).
//
// Lokaler State (1:1 Port aus otto-list-images.ts renderParentMatrix):
//   order: Map<resourceId, Record<col, rank>>  - col = PARENT_COL für die Vater-
//   Spalte (→ parentArticleId beim Speichern), sonst die Kind-Artikel-ID; rank
//   1-basiert je Spalte. Initialisiert aus assignedOrder (Backend, sort_order).
// Gambio-Spezifika ggü. Otto: eigener Speichern-Button (#kli-mx-save, kein
// Save-Registry-Pane), Upload-Zone zum Variantenvater, SEO-Titel-Spalte je Bild.
// ============================================================================
async function renderParentMatrix(host: HTMLElement, ctx: ImagesTabCtx, parentListingId: string): Promise<void> {
  host.innerHTML = `<style>${COMPONENT_STYLES}${MATRIX_STYLES}</style><div class="kli-shell"><div class="kli-empty">Lade Bild-Zuordnung …</div></div>`;
  const res = await callFn<{ ok: boolean; targets: MatrixTarget[]; images: MatrixImage[]; languages?: string[] }>(
    ctx, 'gambio-variant-image-matrix', { action: 'list', parentListingId });
  if (!res.ok) { host.innerHTML = `<div class="kli-shell"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`; return; }
  const targets = res.value.targets;
  const images = res.value.images;
  const languages = sortLangs(res.value.languages ?? ['de', 'en']);
  const parentArticleId = targets.find((t) => t.isParent)?.articleId ?? '';

  // Spalten-Keys: Vater → PARENT_COL, Varianten-Kinder → ihre Artikel-ID. Reihenfolge =
  // targets-Reihenfolge (Vater zuerst). colKeyFor()/articleIdForCol() mappen hin und her.
  const PARENT_COL = '__parent__';
  const colKeyFor = (t: MatrixTarget): string => (t.isParent ? PARENT_COL : t.articleId);
  const articleIdForCol = (col: string): string => (col === PARENT_COL ? parentArticleId : col);
  const cols = targets.map(colKeyFor);

  // order: pro Bild ein Record<col, rank>. Init aus assignedOrder (Backend liefert
  // sort_order je Ziel-Artikel-ID; Vater-Artikel-ID → PARENT_COL). seo: SEO-Titel je
  // Bild (Gambio-spezifisch, gilt kanal-uebergreifend fuers Bild, nicht je Ziel).
  const order = new Map<string, Record<string, number>>();
  const seo = new Map<string, Record<string, string>>();
  for (const img of images) {
    seo.set(img.resourceId, { ...(img.seoTitleByLang ?? {}) });
    const rec: Record<string, number> = {};
    const ao = img.assignedOrder ?? {};
    for (const t of targets) {
      const aid = t.articleId;
      const col = colKeyFor(t);
      if (Object.prototype.hasOwnProperty.call(ao, aid)) {
        rec[col] = ao[aid]!;
      } else if (img.assignedTo.includes(aid)) {
        // Fallback: zugeordnet, aber (noch) kein sort_order → ans Ende einreihen.
        rec[col] = Number.MAX_SAFE_INTEGER;
      }
    }
    order.set(img.resourceId, rec);
  }
  // Ränge je Spalte auf 1..N normalisieren (Backend liefert evtl. 0-basiert/lückig;
  // unser Cell-Model erwartet 1-basierte, lückenlose Ränge pro Spalte).
  for (const col of cols) {
    images
      .filter((im) => order.get(im.resourceId)?.[col] !== undefined)
      .sort((a, b) => (order.get(a.resourceId)![col]!) - (order.get(b.resourceId)![col]!))
      .forEach((im, idx) => { order.get(im.resourceId)![col] = idx + 1; });
  }

  let dirty = false;
  const previewCache = new Map<string, string>();
  const updateSave = () => { const b = host.querySelector<HTMLButtonElement>('#kli-mx-save'); if (b) b.disabled = !dirty; };

  // ---- Order-Cell-Algorithmus (Port aus otto-list-images.ts) ----
  function colMax(col: string): number {
    let m = 0;
    for (const im of images) { const r = order.get(im.resourceId)?.[col]; if (r && r > m) m = r; }
    return m;
  }
  function ordAdd(rid: string, col: string): void {
    const rec = order.get(rid)!; rec[col] = colMax(col) + 1;
    dirty = true;
  }
  function ordRm(rid: string, col: string): void {
    const rec = order.get(rid)!; const r = rec[col];
    if (r === undefined) return;
    delete rec[col];
    for (const im of images) {
      const orec = order.get(im.resourceId)!;
      if (orec[col] !== undefined && orec[col]! > r) orec[col]!--;
    }
    dirty = true;
  }
  function ordSwap(rid: string, col: string, dir: 1 | -1): void {
    const rec = order.get(rid)!; const r = rec[col];
    if (r === undefined) return;
    const partner = images.find((im) => order.get(im.resourceId)?.[col] === r + dir);
    if (!partner) return;
    order.get(partner.resourceId)![col] = r;
    rec[col] = r + dir;
    dirty = true;
  }

  function ordCellHtml(rid: string, col: string): string {
    const rank = order.get(rid)?.[col];
    if (rank) {
      const isLast = rank === colMax(col);
      return `<span class="kl-ordwrap">`
        + `<button type="button" class="kl-ordcell is-on" data-ordrm data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="Zuordnung entfernen">${rank}</button>`
        + `<span class="kl-ordmini">`
        + `<button type="button" data-ordup data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="nach vorne" ${rank <= 1 ? 'disabled' : ''}><span class="v-icon fa-solid fa-chevron-up"></span></button>`
        + `<button type="button" data-orddown data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="nach hinten" ${isLast ? 'disabled' : ''}><span class="v-icon fa-solid fa-chevron-down"></span></button>`
        + `</span></span>`;
    }
    return `<button type="button" class="kl-ordcell" data-ordadd data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="zuordnen">+</button>`;
  }

  function renderMatrix(body: HTMLElement): void {
    if (images.length === 0) {
      body.innerHTML = `<div class="kli-empty">Keine Bilder an Vater oder Varianten. Bitte in Vario unter Artikel-Detail → Anlagen Bilder hochladen.</div>`;
      return;
    }
    const colHead = targets.map((t) => {
      const col = colKeyFor(t);
      const allOn = images.every((im) => order.get(im.resourceId)?.[col] !== undefined);
      return `<th><label class="kl-colcb ${t.isParent ? 'is-vater' : ''}"><input type="checkbox" class="kl-mcb kli-mx-colhead" data-col="${escapeHtml(col)}" ${allOn ? 'checked' : ''}>${escapeHtml(t.label)}</label></th>`;
    }).join('');
    const rowsHtml = images.map((img) => {
      // Anzeigeweg wie im Bilder-Tab: Cache, dann DIREKT aus dem DMS, dann Proxy. Die Vater-Matrix
      // hat ihren EIGENEN Thumbnail-Pfad — beim Umstellen am 27.08.2026 blieb er zurueck, und die
      // Ladezeit blieb lang. `loadMatrixPreviews` arbeitet sequenziell (ein Vario-Call + OAuth je
      // Bild); bei einer Variantenfamilie stehen hier leicht 13 Bilder.
      const ready = previewCache.get(img.resourceId) ?? null;
      const direkt = ready ? null : varioPublicDmsUrl(img.publicUrl);
      const thumb = ready
        ? `<img class="kl-mthumb-img" src="${escapeHtml(ready)}" alt="">`
        : direkt
          ? `<div class="kl-mthumb" data-rid="${escapeHtml(img.resourceId)}" data-aid="${escapeHtml(img.owningArticleId)}">`
            + `<img class="kl-mthumb-img img-dms-direct" src="${escapeHtml(direkt)}" alt=""></div>`
          : `<div class="kl-mthumb kli-thumb-loading img-proxy-lazy" data-rid="${escapeHtml(img.resourceId)}" data-aid="${escapeHtml(img.owningArticleId)}"><span class="v-icon fa-light fa-image"></span></div>`;      const cells = targets.map((t) => `<td class="kli-mx-center">${ordCellHtml(img.resourceId, colKeyFor(t))}</td>`).join('');
      return `<tr>
        <td class="kli-mx-imgcell">${thumb}</td>
        <td class="kli-mx-metacell"><button type="button" class="kl-malle kli-mx-all" data-rid="${escapeHtml(img.resourceId)}" title="Allen Zielen zuordnen">alle</button><div class="kl-mdms" title="${escapeHtml(img.alias)}">${escapeHtml(img.alias)}</div></td>
        <td class="kli-mx-seocell">${altLangInputs(img.resourceId, seo.get(img.resourceId) ?? {}, languages, 'SEO-Titel', 'kli-mx-seo')}</td>
        ${cells}
      </tr>`;
    }).join('');
    // Horizontale Scroll-Position über den Full-Re-Render hinweg erhalten - sonst springt
    // die Matrix bei jeder Zell-Aktion zurück nach links (Bug bei vielen Varianten-Spalten).
    const prevScroll = body.querySelector<HTMLElement>('.kli-matrix-wrap')?.scrollLeft ?? 0;
    body.innerHTML = `
      <div class="kli-img-note">Klicke eine Zelle, um das Bild der Variante zuzuordnen - die Zahl ist die <b>Anzeige-Reihenfolge je Variante</b>. Mit ▲▼ sortieren, Klick auf die Zahl entfernt die Zuordnung.</div>
      <div class="kli-matrix-wrap"><table class="kli-matrix"><thead><tr><th class="kl-mx-lft">Bild</th><th class="kl-mx-lft"></th><th class="kl-mx-lft">SEO-Titel</th>${colHead}</tr></thead><tbody>${rowsHtml}</tbody></table></div>`;
    const wrap = body.querySelector<HTMLElement>('.kli-matrix-wrap');
    if (wrap && prevScroll) wrap.scrollLeft = prevScroll;
    wireMatrix(body);
    beobachteMatrixBilder(body);
    void loadMatrixPreviews(body);
  }

  function wireMatrix(body: HTMLElement): void {
    // Order-Zellen: zuordnen / entfernen / vor / zurück. Jeder Handler mutiert den
    // State und rerendert die Matrix.
    body.querySelectorAll<HTMLButtonElement>('button[data-ordadd]').forEach((b) => b.addEventListener('click', () => {
      ordAdd(b.dataset['rid']!, b.dataset['col']!); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-ordrm]').forEach((b) => b.addEventListener('click', () => {
      ordRm(b.dataset['rid']!, b.dataset['col']!); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-ordup]').forEach((b) => b.addEventListener('click', () => {
      if (b.disabled) return; ordSwap(b.dataset['rid']!, b.dataset['col']!, -1); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-orddown]').forEach((b) => b.addEventListener('click', () => {
      if (b.disabled) return; ordSwap(b.dataset['rid']!, b.dataset['col']!, 1); renderMatrix(body); updateSave();
    }));
    // Spalten-Kopf-Checkbox: checked = ALLE Bilder in Zeilen-Reihenfolge (rank 1..N)
    // dieser Spalte zuordnen; unchecked = Spalte leeren.
    body.querySelectorAll<HTMLInputElement>('input.kli-mx-colhead').forEach((cb) => cb.addEventListener('change', () => {
      const col = cb.dataset['col']!;
      if (cb.checked) { let r = 1; for (const im of images) order.get(im.resourceId)![col] = r++; }
      else { for (const im of images) delete order.get(im.resourceId)![col]; }
      dirty = true; renderMatrix(body); updateSave();
    }));
    // Per-Zeile "alle": dieses Bild ALLEN Spalten zuordnen (je colMax(col)+1, sofern
    // noch nicht zugeordnet).
    body.querySelectorAll<HTMLButtonElement>('button.kli-mx-all').forEach((b) => b.addEventListener('click', () => {
      const rid = b.dataset['rid']!; const rec = order.get(rid)!;
      for (const col of cols) { if (rec[col] === undefined) rec[col] = colMax(col) + 1; }
      dirty = true; renderMatrix(body); updateSave();
      showToast('Bild allen Varianten zugeordnet', 'success');
    }));
    body.querySelectorAll<HTMLInputElement>('input.kli-mx-seo').forEach((inp) => inp.addEventListener('input', () => {
      const rid = inp.dataset['rid']!; const lang = inp.dataset['lang'];
      if (!lang) return;
      const rec = seo.get(rid) ?? {};
      rec[lang] = inp.value; seo.set(rid, rec);
      dirty = true; updateSave();
    }));
  }

  /**
   * Faengt eine fehlgeschlagene DIREKTE DMS-Adresse ab und schiebt die Zelle in den Proxy-Pfad.
   * `error` steigt nicht auf, deshalb Capture-Phase. Der Platzhalter ist hier die `.kl-mthumb`-Huelle
   * mit `data-rid`/`data-aid` — also muss die HUELLE markiert werden, nicht das Bild.
   * Nur EIN Versuch je Bild (`img-dms-direct` fliegt weg), sonst dreht es sich im Kreis.
   */
  function beobachteMatrixBilder(body: HTMLElement): void {
    body.addEventListener('error', (ev) => {
      const el = ev.target as HTMLElement | null;
      if (!(el instanceof HTMLImageElement) || !el.classList.contains('img-dms-direct')) return;
      el.classList.remove('img-dms-direct');
      const huelle = el.closest<HTMLElement>('.kl-mthumb');
      if (!huelle?.dataset['rid'] || !huelle.dataset['aid']) return;
      huelle.classList.add('kli-thumb-loading', 'img-proxy-lazy');
      huelle.innerHTML = '';
      void loadMatrixPreviews(body);
    }, true);
  }

  async function loadMatrixPreviews(body: HTMLElement): Promise<void> {
    for (const ph of Array.from(body.querySelectorAll<HTMLElement>('.img-proxy-lazy'))) {
      const rid = ph.dataset['rid']; const aid = ph.dataset['aid'];
      if (!rid || !aid || !ph.isConnected) continue;
      const setImg = (url: string): void => {
        if (!ph.isConnected) return;
        ph.classList.remove('kli-thumb-loading');
        ph.innerHTML = `<img class="kl-mthumb-img" src="${escapeHtml(url)}" alt="">`;
      };
      const cached = previewCache.get(rid);
      if (cached) { setImg(cached); continue; }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: aid, resourceId: rid, marketplace: 'gambio' }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob()); previewCache.set(rid, url);
        setImg(url);
      } catch (e) { console.warn('[gambio-list-images] matrix preview proxy failed for', rid, (e as Error).message); }
    }
  }

  async function doSaveMatrix(): Promise<void> {
    const btn = host.querySelector<HTMLButtonElement>('#kli-mx-save'); if (btn) btn.disabled = true;
    // Aus dem order-Map die geordneten Ziele je Bild bauen: PARENT_COL → parentArticleId,
    // sortOrder = der 1-basierte Rang (Backend speichert ihn als sort_order, niedriger = früher).
    const assignments = images.map((img) => {
      const rec = order.get(img.resourceId) ?? {};
      const tgts = Object.entries(rec).map(([col, rank]) => ({ articleId: articleIdForCol(col), sortOrder: rank }));
      return {
        resourceId: img.resourceId,
        mimeType: img.mimeType,
        alias: img.alias,
        seoTitleByLang: seo.get(img.resourceId) ?? {},
        targets: tgts,
      };
    });
    const save = await callFn<{ ok: boolean; enqueuedShareJobs: number }>(ctx, 'gambio-variant-image-matrix', { action: 'save', parentListingId, assignments });
    if (!save.ok) {
      openErrorModal({
        title: 'Speichern fehlgeschlagen',
        items: [{ message: save.error }],
        context: { action: 'gambio-variant-image-matrix/save', parentListingId, assignmentCount: assignments.length },
        json: save,
      });
      if (btn) btn.disabled = false;
      return;
    }
    showToast(`Zuordnung gespeichert (${save.value.enqueuedShareJobs} Bild-Freigaben aktualisiert).`, 'success');
    dirty = false; void renderParentMatrix(host, ctx, parentListingId);
  }

  // Upload-Zone für den Variantenvater. Neues Bild landet im Vater-DMS
  // (vario-article-image-upload mit parentArticleId) und erscheint nach Reload als
  // neue Matrix-Zeile zum Zuordnen. Kinder laden im eigenen (Standalone-)Listing hoch.
  const uploadZoneHtml = parentArticleId
    ? `<div class="kli-mx-upload" id="kli-mx-upload">
        <div class="kli-upload-zone" id="kli-mx-upload-zone" role="button" tabindex="0" aria-label="Bild zum Variantenhauptartikel hochladen - klicken oder hierher ziehen">
          <input type="file" id="kli-mx-upload-input" accept="image/jpeg,image/png,image/gif,image/webp">
          <span class="v-icon fa-regular fa-cloud-arrow-up" style="font-size:1.4em;color:var(--v-positive,#84b933);"></span>
          <div class="kli-upload-zone-label"><strong>Bild zum Variantenhauptartikel hochladen</strong><br><span>Klicken oder Datei hier ablegen (JPG, PNG, GIF, WebP) - danach unten dem Ziel zuordnen</span></div>
          <span id="kli-mx-upload-busy" hidden><span class="v-icon fa-regular fa-spinner-third fa-spin"></span></span>
        </div>
      </div>`
    : '';
  // Kein Sub-Tab mehr: die Vater-Spalte der Matrix ordnet die Vater-Bilder bereits
  // (order-Zellen je Spalte) - nur noch Upload + Speichern-Button + Matrix.
  host.innerHTML = `
    <style>${COMPONENT_STYLES}${MATRIX_STYLES}</style>
    <div class="kli-shell">
      <div class="kli-mx-toolbar">
        <button type="button" class="v-button v-button--save" id="kli-mx-save" disabled><span class="v-icon fa-regular fa-floppy-disk"></span> Zuordnung speichern</button>
      </div>
      ${uploadZoneHtml}
      <div class="kli-body"></div>
    </div>`;
  host.querySelector<HTMLButtonElement>('#kli-mx-save')?.addEventListener('click', () => void doSaveMatrix());
  wireMatrixUpload();
  const bodyEl = host.querySelector<HTMLElement>('.kli-body')!;
  renderMatrix(bodyEl);

  function wireMatrixUpload(): void {
    const zone = host.querySelector<HTMLElement>('#kli-mx-upload-zone');
    const inp = host.querySelector<HTMLInputElement>('#kli-mx-upload-input');
    const busy = host.querySelector<HTMLElement>('#kli-mx-upload-busy');
    if (!zone || !inp) return;
    const zoneEl: HTMLElement = zone;
    zoneEl.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('input[type=file]')) return; inp.click(); });
    zoneEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); } });
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('kli-upload-zone--drag'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('kli-upload-zone--drag'));
    zoneEl.addEventListener('drop', (e) => { e.preventDefault(); zoneEl.classList.remove('kli-upload-zone--drag'); const f = e.dataTransfer?.files[0]; if (f) void doMatrixUpload(f); });
    inp.addEventListener('change', () => { const f = inp.files?.[0]; if (f) void doMatrixUpload(f); });

    async function doMatrixUpload(file: File): Promise<void> {
      if (!parentArticleId) { showToast('Kein Vater-Artikel ermittelt.', 'error'); return; }
      if (busy) busy.hidden = false;
      zoneEl.style.pointerEvents = 'none';
      try {
        const res = await uploadImageFile(ctx, file, parentArticleId);
        if (!res.ok) { showToast(res.message, 'error'); return; }
        showToast('Bild hochgeladen - jetzt unten dem Ziel zuordnen.', 'success');
        void renderParentMatrix(host, ctx, parentListingId);
      } finally {
        if (busy) busy.hidden = true;
        zoneEl.style.pointerEvents = '';
      }
    }
  }
}

// ============================================================================
// Varianten-Kind: EINE Spalte der Vater-Matrix, editierbar. Zeigt den gemeinsamen
// Bildpool (Vater + alle Geschwister) mit einer Checkbox "dieser Variante zuweisen"
// pro Bild + Alt-Text + Drag&Drop-Reihenfolge + Upload ins Kind-DMS. Liest via
// gambio-variant-image-matrix (action:list, inkl. assignedOrder), speichert via
// gambio-release-images (nur die Zeilen DIESER Variante -> einzel-artikel-sicher).
// ============================================================================
interface VariantRow {
  resourceId: string;
  owningArticleId: string;
  alias: string;
  mimeType: string;
  publicUrl: string | null;
  assigned: boolean;
  altTextByLang: Record<string, string>;
}

async function renderVariantColumn(
  host: HTMLElement,
  ctx: ImagesTabCtx,
  childArticleId: string,
  parentListingId: string,
): Promise<void> {
  host.innerHTML = `<style>${COMPONENT_STYLES}</style><div class="kli-shell"><div class="kli-empty">Lade Bild-Zuordnung …</div></div>`;
  const res = await callFn<{ ok: boolean; targets: MatrixTarget[]; images: MatrixImage[]; languages?: string[] }>(
    ctx, 'gambio-variant-image-matrix', { action: 'list', parentListingId });
  if (!res.ok) { host.innerHTML = `<div class="kli-shell"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`; return; }
  const languages = sortLangs(res.value.languages ?? ['de', 'en']);

  // Pool -> VariantRow[]. Angehakte (assigned) zuerst nach assignedOrder[child],
  // danach die restlichen Pool-Bilder in Pool-Reihenfolge (stabiler Tiebreaker).
  const poolIndex = new Map<string, number>();
  res.value.images.forEach((img, i) => poolIndex.set(img.resourceId, i));
  const rows: VariantRow[] = res.value.images.map((img) => ({
    resourceId: img.resourceId,
    owningArticleId: img.owningArticleId,
    alias: img.alias,
    mimeType: img.mimeType,
    publicUrl: img.publicUrl,
    assigned: img.assignedTo.includes(childArticleId),
    altTextByLang: { ...(img.seoTitleByLang ?? {}) },
  }));
  rows.sort((a, b) => {
    if (a.assigned !== b.assigned) return a.assigned ? -1 : 1;
    if (a.assigned) {
      const imgA = res.value.images.find((x) => x.resourceId === a.resourceId)!;
      const imgB = res.value.images.find((x) => x.resourceId === b.resourceId)!;
      const oa = imgA.assignedOrder[childArticleId] ?? 0;
      const ob = imgB.assignedOrder[childArticleId] ?? 0;
      if (oa !== ob) return oa - ob;
    }
    return (poolIndex.get(a.resourceId) ?? 0) - (poolIndex.get(b.resourceId) ?? 0);
  });

  let dirty = false;
  const previewCache = new Map<string, string>();

  function buildReleases(): Array<{ varioResourceId: string; sortOrder: number; mimeType: string; alias: string; altTextByLang: Record<string, string> }> {
    return rows
      .filter((r) => r.assigned)
      .map((r, idx) => ({
        varioResourceId: r.resourceId,
        sortOrder: idx,
        mimeType: r.mimeType,
        alias: r.alias,
        altTextByLang: r.altTextByLang,
      }));
  }

  function previewImg(r: VariantRow): string {
    // Anzeigeweg wie im Bilder-Tab und in der Vater-Matrix: Cache, dann DIREKT aus dem DMS, dann
    // Proxy. Dritter eigener Thumbnail-Pfad in dieser Datei — beim Umstellen alle drei anfassen,
    // sonst bleibt einer sequenziell und die Ladezeit bleibt lang (so passiert am 27.08.2026).
    const ready = previewCache.get(r.resourceId) ?? null;
    if (ready) return `<img class="kli-thumb" src="${escapeHtml(ready)}" alt="" draggable="false">`;
    const direkt = varioPublicDmsUrl(r.publicUrl);
    if (direkt) {
      return `<img class="kli-thumb img-dms-direct" src="${escapeHtml(direkt)}" `
        + `data-rid="${escapeHtml(r.resourceId)}" data-aid="${escapeHtml(r.owningArticleId)}" alt="" draggable="false">`;
    }
    return `<img class="kli-thumb kli-thumb-loading img-proxy-lazy" data-rid="${escapeHtml(r.resourceId)}" data-aid="${escapeHtml(r.owningArticleId)}" alt="" draggable="false">`;
  }

  // Grüner Positions-Button (wie in der Vater-Matrix): zugewiesen → grüne Positionsnummer
  // (Klick entfernt), sonst „+" (Klick weist zu). Reihenfolge weiterhin per Drag & Drop.
  function renderRow(r: VariantRow, pos: number | null): string {
    const ordBtn = pos !== null
      ? `<button type="button" class="kl-ordcell is-on" data-var-toggle data-rid="${escapeHtml(r.resourceId)}" draggable="false" title="Dieser Variante zugeordnet (Position ${pos}) - klicken zum Entfernen">${pos}</button>`
      : `<button type="button" class="kl-ordcell" data-var-toggle data-rid="${escapeHtml(r.resourceId)}" draggable="false" title="Dieser Variante zuweisen">+</button>`;
    return `
      <div class="kli-row kli-row--variant" draggable="true" data-rid="${escapeHtml(r.resourceId)}">
        <span class="kli-drag" title="Ziehen zum Sortieren"><span class="v-icon fa-regular fa-grip-vertical"></span></span>
        <span class="kli-active-wrap">${ordBtn}</span>
        ${previewImg(r)}
        <div class="kli-meta">
          <div class="kli-alias" title="${escapeHtml(r.alias)}">${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}</div>
          ${altLangInputs(r.resourceId, r.altTextByLang, languages, 'Alt-Text')}
        </div>
      </div>`;
  }

  function renderUploadZone(): string {
    return `
      <div class="kli-upload-zone" id="kli-var-upload-zone" role="button" tabindex="0"
           aria-label="Bild zu dieser Variante hochladen - klicken oder hierher ziehen">
        <input type="file" id="kli-var-upload-input" accept="image/jpeg,image/png,image/gif,image/webp">
        <span class="v-icon fa-regular fa-cloud-arrow-up" style="font-size:1.4em;color:var(--v-positive,#84b933);"></span>
        <div class="kli-upload-zone-label">
          <strong>Bild zu dieser Variante hochladen</strong><br>
          <span>Klicken oder Datei hier ablegen (JPG, PNG, GIF, WebP) - danach mit „+" zuordnen</span>
        </div>
        <span id="kli-var-upload-busy" hidden><span class="v-icon fa-regular fa-spinner-third fa-spin"></span></span>
      </div>`;
  }

  function rerenderAll(): void {
    const body = host.querySelector<HTMLElement>('.kli-body');
    if (!body) return;
    const assignedCount = rows.filter((r) => r.assigned).length;
    if (rows.length === 0) {
      body.innerHTML = `
        <div class="kli-header"><strong>Bilder dieser Variante</strong> ${BILDER_BADGE}</div>
        ${renderUploadZone()}
        <div class="kli-empty">Keine Bilder an Vater oder Varianten. Bitte in Vario unter Artikel-Detail → Anlagen Bilder hochladen.</div>`;
      wireUploadZone(body);
      return;
    }
    body.innerHTML = `
      <div class="kli-header">
        <div>
          <strong>Bilder dieser Variante</strong> ${BILDER_BADGE}
          <span class="v-text-muted" style="font-size:0.85em;margin-left:0.5em;">${assignedCount} von ${rows.length} ${rows.length === 1 ? 'Bild' : 'Bildern'} zugewiesen</span>
        </div>
        <div style="display:flex;gap:0.4em;">
          <button type="button" class="v-button" id="kli-var-reset" ${dirty ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-rotate-left"></span> Zurücksetzen
          </button>
          <button type="button" class="v-button v-button--save" id="kli-var-save" ${dirty ? '' : 'disabled'}>
            <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
          </button>
        </div>
      </div>
      ${renderUploadZone()}
      <div class="kli-list">${(() => { let p = 0; return rows.map((r) => renderRow(r, r.assigned ? ++p : null)).join(''); })()}</div>`;
    wireRows(body);
    wireUploadZone(body);
    beobachteVariantenBilder(body);
    void loadPreviews(body);
  }

  function wireRows(body: HTMLElement): void {
    body.querySelectorAll<HTMLButtonElement>('button[data-var-toggle]').forEach((btn) => btn.addEventListener('click', () => {
      const r = rows.find((x) => x.resourceId === btn.dataset['rid']);
      if (!r) return;
      r.assigned = !r.assigned;
      dirty = true;
      rerenderAll();
    }));
    body.querySelectorAll<HTMLInputElement>('input.kli-alt-input').forEach((inp) => inp.addEventListener('input', () => {
      const r = rows.find((x) => x.resourceId === inp.dataset['rid']);
      const lang = inp.dataset['lang'];
      if (r && lang) { r.altTextByLang[lang] = inp.value; dirty = true; updateSaveBtn(body); }
    }));
    wireDragAndDrop(body);
    body.querySelector<HTMLButtonElement>('#kli-var-save')?.addEventListener('click', () => void doSave());
    body.querySelector<HTMLButtonElement>('#kli-var-reset')?.addEventListener('click', () => void renderVariantColumn(host, ctx, childArticleId, parentListingId));
  }

  function updateSaveBtn(body: HTMLElement): void {
    const save = body.querySelector<HTMLButtonElement>('#kli-var-save');
    const reset = body.querySelector<HTMLButtonElement>('#kli-var-reset');
    if (save) save.disabled = !dirty;
    if (reset) reset.disabled = !dirty;
  }

  function wireDragAndDrop(body: HTMLElement): void {
    const list = body.querySelector<HTMLElement>('.kli-list');
    if (!list) return;
    let draggedRid: string | null = null;
    list.querySelectorAll<HTMLElement>('.kli-row').forEach((rowEl) => {
      rowEl.addEventListener('dragstart', (ev) => {
        draggedRid = rowEl.dataset['rid'] ?? null;
        rowEl.classList.add('kli-row--dragging');
        if (ev.dataTransfer) { ev.dataTransfer.effectAllowed = 'move'; ev.dataTransfer.setData('text/plain', draggedRid ?? ''); }
      });
      rowEl.addEventListener('dragend', () => {
        rowEl.classList.remove('kli-row--dragging');
        list.querySelectorAll('.kli-row--drag-over').forEach((el) => el.classList.remove('kli-row--drag-over'));
        draggedRid = null;
      });
      rowEl.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
        if (rowEl.dataset['rid'] !== draggedRid) rowEl.classList.add('kli-row--drag-over');
      });
      rowEl.addEventListener('dragleave', () => rowEl.classList.remove('kli-row--drag-over'));
      rowEl.addEventListener('drop', (ev) => {
        ev.preventDefault();
        rowEl.classList.remove('kli-row--drag-over');
        const targetRid = rowEl.dataset['rid'] ?? null;
        const sourceRid = draggedRid ?? ev.dataTransfer?.getData('text/plain') ?? null;
        if (!sourceRid || !targetRid || sourceRid === targetRid) return;
        const fromIdx = rows.findIndex((r) => r.resourceId === sourceRid);
        const toIdx = rows.findIndex((r) => r.resourceId === targetRid);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
        const [moved] = rows.splice(fromIdx, 1);
        rows.splice(toIdx, 0, moved!);
        dirty = true;
        rerenderAll();
      });
    });
  }

  /** Fehlgeschlagene DIREKTE Adresse EINMAL in den Proxy-Pfad schieben (Capture-Phase, `error`
   *  steigt nicht auf). Hier ist der Platzhalter das Bild selbst, nicht eine Huelle. */
  function beobachteVariantenBilder(body: HTMLElement): void {
    body.addEventListener('error', (ev) => {
      const el = ev.target as HTMLElement | null;
      if (!(el instanceof HTMLImageElement) || !el.classList.contains('img-dms-direct')) return;
      el.classList.remove('img-dms-direct');
      el.classList.add('kli-thumb-loading', 'img-proxy-lazy');
      el.removeAttribute('src');
      void loadPreviews(body);
    }, true);
  }

  async function loadPreviews(body: HTMLElement): Promise<void> {
    for (const imgEl of Array.from(body.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy'))) {
      const rid = imgEl.dataset['rid']; const aid = imgEl.dataset['aid'];
      if (!rid || !aid || !imgEl.isConnected) continue;
      const cached = previewCache.get(rid);
      if (cached) { imgEl.src = cached; imgEl.classList.remove('kli-thumb-loading'); continue; }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: aid, resourceId: rid, marketplace: 'gambio' }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob()); previewCache.set(rid, url);
        if (imgEl.isConnected) { imgEl.src = url; imgEl.classList.remove('kli-thumb-loading'); }
      } catch (e) { console.warn('[gambio-list-images] variant preview proxy failed for', rid, (e as Error).message); }
    }
  }

  function wireUploadZone(body: HTMLElement): void {
    const zone = body.querySelector<HTMLElement>('#kli-var-upload-zone');
    const inp = body.querySelector<HTMLInputElement>('#kli-var-upload-input');
    const busy = body.querySelector<HTMLElement>('#kli-var-upload-busy');
    if (!zone || !inp) return;
    const zoneEl: HTMLElement = zone;
    zoneEl.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('input[type=file]')) return; inp.click(); });
    zoneEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); } });
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('kli-upload-zone--drag'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('kli-upload-zone--drag'));
    zoneEl.addEventListener('drop', (e) => { e.preventDefault(); zoneEl.classList.remove('kli-upload-zone--drag'); const f = e.dataTransfer?.files[0]; if (f) void doUpload(f); });
    inp.addEventListener('change', () => { const f = inp.files?.[0]; if (f) void doUpload(f); });

    async function doUpload(file: File): Promise<void> {
      if (busy) busy.hidden = false;
      zoneEl.style.pointerEvents = 'none';
      try {
        const up = await uploadImageFile(ctx, file, childArticleId);
        if (!up.ok) { showToast(up.message, 'error'); return; }
        showToast('Bild hochgeladen - jetzt mit „+" zuordnen und speichern.', 'success');
        void renderVariantColumn(host, ctx, childArticleId, parentListingId);
      } finally {
        if (busy) busy.hidden = true;
        zoneEl.style.pointerEvents = '';
      }
    }
  }

  async function doSave(): Promise<void> {
    const btn = host.querySelector<HTMLButtonElement>('#kli-var-save');
    if (btn) btn.disabled = true;
    const releases = buildReleases();
    const save = await callFn<{ ok: boolean; inserted: number; updated: number; deleted: number; enqueuedShareJobs?: number }>(
      ctx, 'gambio-release-images', { articleId: childArticleId, releases });
    if (!save.ok) {
      openErrorModal({
        title: 'Speichern fehlgeschlagen',
        items: [{ message: save.error }],
        context: { action: 'gambio-release-images', articleId: childArticleId, releaseCount: releases.length },
        json: save,
      });
      if (btn) btn.disabled = false;
      return;
    }
    const v = save.value;
    showToast(`Zuweisung gespeichert (${v.inserted} neu, ${v.updated} sortiert, ${v.deleted} entfernt).`, 'success');
    dirty = false;
    void renderVariantColumn(host, ctx, childArticleId, parentListingId);
  }

  host.innerHTML = `
    <style>${COMPONENT_STYLES}${MATRIX_STYLES}</style>
    <div class="kli-shell">
      <div class="v-banner v-banner-info" style="margin:0 0 0.4em;"><span class="v-icon fa-light fa-circle-info"></span><div>Hier legst du fest, welche Bilder aus dem gemeinsamen Bildpool (Variantenhauptartikel + andere Varianten) zu <strong>dieser Variante</strong> gehören. Die Zahl im grünen Button ist die <strong>Anzeige-Position dieses Bildes im Shop</strong> (für diese Variante); „+" ordnet ein Bild zu. Bilder verwaltest/löschst du weiterhin im Vario-DMS.</div></div>
      <div class="kli-body"></div>
    </div>`;
  rerenderAll();
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
